<template>
  <!-- Alternatives v2: the alternatives template in the shipped compare template's
       vocabulary. Light split hero with the real app (inbox: the texting the CTA sells), the answer
       first, the table straight after it (stacked on phones, no sideways scroll), tools as ruled
       disclosures with every word still in the page, one closing CTA. 4-10px corners, no pills. -->
  <main class="grow av">
    <header class="av-hero">
      <div class="av-wrap av-hero-grid">
        <div class="av-hero-top">
          <nav class="av-crumb" aria-label="Breadcrumb">
            <NuxtLink :to="localePath('/')">Home</NuxtLink>
            <span aria-hidden="true">/</span>
            <NuxtLink :to="localePath('/alternatives')">Alternatives</NuxtLink>
            <span aria-hidden="true">/</span>
            <span aria-current="page">{{ page.eyebrow }}</span>
          </nav>
          <h1 v-if="h1Parts"><span class="av-h1-main">{{ h1Parts[0] }}</span><span class="av-h1-rest"><span class="sr-only">: </span>{{ h1Parts[1] }}</span></h1>
          <h1 v-else>{{ page.h1 }}</h1>
        </div>
        <div class="av-hero-copy">
          <p v-if="page.answerSummary" class="av-answer">{{ page.answerSummary }}</p>
          <div class="av-actions">
            <a
              :href="signupUrl('https://app.workspace369.com/signup?plan=voyager')"
              class="av-btn"
              data-conversion-event="start_free_click"
              :data-conversion-label="`${page.h1} hero CTA (Voyager)`"
              :data-conversion-location="location"
              :data-conversion-variant="ctaVariant || undefined"
              :style="ctaEnrolled ? { minWidth: 'min(18em, 100%)' } : null"
            >{{ ctaEnrolled ? ctaLabel : 'Start free with Voyager — SMS built in' }}</a>
          </div>
          <TrialDisclosure plan="voyager" />
          <ChatGptProof :location="location" />
          <p class="av-after">
            <NuxtLink
              :to="localePath('/pricing')"
              class="av-link"
              data-conversion-event="pricing_click"
              :data-conversion-label="`${page.h1} hero pricing link`"
              :data-conversion-location="location"
            >Compare plans →</NuxtLink>
          </p>
          <p class="av-byline">
            <Logo class="av-byline-logo" aria-hidden="true" />
            <span>
              {{ page.author.name }}, {{ page.author.role }} · Published
              <time :datetime="page.publishedIso">{{ page.published }}</time> · Updated
              <time :datetime="page.updatedIso">{{ page.updated }}</time> ·
              <a href="#av-method">How we compared</a>
            </span>
          </p>
        </div>
        <!-- The real app: the Workspace369 inbox with a client's texts and calls (real screenshot). -->
        <figure class="av-hero-shot">
          <!-- Files are cropped to the frame (nothing out of frame ships). Desktop: list + thread, caller numbers blurred; phones: the SMS thread, readable at 390. -->
          <picture class="av-shot-light">
            <source media="(max-width: 767px)" srcset="/images/alternatives/inbox-thread-light.webp" width="1600" height="1200" />
            <img src="/images/alternatives/inbox-light.webp" alt="Workspace369 inbox: an SMS thread with a client beside the conversation list." width="1151" height="863" fetchpriority="high" />
          </picture>
          <picture class="av-shot-dark">
            <source media="(max-width: 767px)" srcset="/images/alternatives/inbox-thread-dark.webp" width="1600" height="1200" />
            <img src="/images/alternatives/inbox-dark.webp" alt="Workspace369 inbox: an SMS thread with a client beside the conversation list." width="1151" height="863" loading="lazy" />
          </picture>
        </figure>
      </div>
      <div class="av-wrap av-hero-note">
        <CompetitorNotice :competitor="page.competitor" :date="page.updated" :iso="page.updatedIso" />
      </div>
    </header>

    <!-- AUDIENCE PICKS (optional): one short, visible answer per buyer the page is asked about,
         so an answer engine can lift it without opening the collapsed scenario list. -->
    <section v-if="page.audiencePicks" class="av-sec" aria-labelledby="av-picks-title">
      <div class="av-wrap av-narrow">
        <h2 id="av-picks-title">{{ page.audiencePicks.title }}</h2>
        <div v-for="pick in page.audiencePicks.items" :key="pick.heading" class="av-pick">
          <h3>{{ pick.heading }}</h3>
          <p>{{ pick.text }}</p>
        </div>
        <p v-if="page.audiencePicks.note" class="av-note">{{ page.audiencePicks.note }}</p>
      </div>
    </section>

    <!-- TABLE: desktop table; phones get one stacked block per tool, so nothing scrolls sideways. -->
    <section id="av-table" class="av-sec av-tint" aria-labelledby="av-table-title">
      <div class="av-wrap av-wide">
        <h2 id="av-table-title">{{ page.competitor }} alternatives compared</h2>
        <p class="av-sub">{{ page.lede }}</p>
        <p class="av-sub">The two columns that decide most switches are SMS and voice, and built-in accounting. {{ page.competitor }} is the first row for context. A dash means the tool's pricing page didn't list the feature on the date under its name; it links to the page we read.</p>
        <table class="av-table">
          <thead>
            <tr>
              <th scope="col">Tool</th>
              <th v-for="column in tableColumns" :key="column.key" scope="col">{{ column.label }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="tool in page.tools" :key="tool.name" :class="{ 'av-us': tool.isUs, 'av-base': tool.baseline }">
              <th scope="row">
                <span class="av-tool">{{ tool.name }}</span>
                <span v-if="tool.baseline" class="av-tag">current tool</span>
                <span v-if="tool.isUs" class="av-tag av-tag-us">ours</span>
                <span class="av-tool-meta">
                  <a v-if="isExternal(tool.pricingUrl)" :href="tool.pricingUrl" target="_blank" rel="noopener noreferrer">pricing ↗</a>
                  <NuxtLink v-else :to="localePath(tool.pricingUrl)">pricing →</NuxtLink>
                  <span v-if="tool.checkedOn"> · checked {{ tool.checkedOn }}</span>
                </span>
              </th>
              <td v-for="column in tableColumns" :key="column.key">
                <span class="av-m">{{ column.label }}</span>
                <template v-if="tool[column.key] === 'yes'"><span class="av-yes">Yes</span></template>
                <template v-else-if="tool[column.key] === 'no'">
                  <a v-if="!tool.isUs && isExternal(tool.pricingUrl)" :href="tool.pricingUrl" target="_blank" rel="noopener noreferrer" class="av-dash" :title="notListedLabel(tool)">—<span class="sr-only">{{ notListedLabel(tool) }}</span></a>
                  <span v-else class="av-dash">—<span class="sr-only">Not included</span></span>
                </template>
                <template v-else>{{ tool[column.key] }}</template>
              </td>
            </tr>
          </tbody>
        </table>
        <p v-if="page.photographerNote" class="av-note">{{ page.photographerNote }}</p>
        <p class="av-note">{{ page.tableNote }}</p>
        <details class="av-more">
          <summary>Prices by scenario, in full</summary>
          <ul class="av-quick">
            <li v-for="(paragraph, i) in page.quickAnswer" :key="i">
              <template v-if="typeof paragraph === 'string'">{{ paragraph }}</template>
              <template v-else-if="paragraph.parts">
                <template v-for="(part, j) in paragraph.parts" :key="j">
                  <template v-if="typeof part === 'string'">{{ part }}</template>
                  <NuxtLink v-else :to="localePath(part.path)">{{ part.label }}</NuxtLink>
                </template>
              </template>
              <template v-else>{{ paragraph.text }}<NuxtLink :to="localePath(paragraph.link.path)">{{ paragraph.link.label }}</NuxtLink>{{ paragraph.after }}</template>
            </li>
          </ul>
        </details>
      </div>
    </section>

    <ComparisonVideo :path="location" :canonical-url="canonicalUrl" :page-id="`${canonicalUrl}#webpage`" article tint plain />

    <!-- TOOLS: one ruled disclosure per tool. The summary carries best-for and price; the full write-up
         (wins, limits, quote, skip-if) is in the page either way. -->
    <section class="av-sec" aria-labelledby="av-tools-title">
      <div class="av-wrap av-narrow">
        <h2 id="av-tools-title">The alternatives, in detail</h2>
        <div class="av-tools">
          <details v-for="tool in alternativeTools" :id="`av-tool-${slugify(tool.name)}`" :key="tool.name" class="av-tool-row" :class="{ 'av-tool-us': tool.isUs }">
            <summary>
              <span class="av-tool-head">
                <strong>{{ tool.name }}</strong>
                <span>{{ tool.bestFor }}</span>
                <span v-if="tool.limitations?.length" class="av-tool-limit"><b>Limitation:</b> {{ tool.limitations[0] }}</span>
              </span>
              <span class="av-tool-price">{{ tool.price }}</span>
            </summary>
            <div class="av-tool-body">
              <p><strong>Pricing:</strong> {{ tool.pricingDetail }} <a :href="tool.officialUrl" target="_blank" rel="noopener noreferrer" class="av-src">{{ hostOf(tool.officialUrl) }} ↗</a></p>
              <div class="av-two">
                <div>
                  <h3>Where it wins</h3>
                  <ul><li v-for="item in tool.strengths" :key="item">{{ item }}</li></ul>
                </div>
                <div>
                  <h3>Real limitations</h3>
                  <ul><li v-for="item in tool.limitations" :key="item">{{ item }}</li></ul>
                </div>
              </div>
              <blockquote v-if="tool.quote && !tool.quote.needsReview" class="av-quote">
                <p>“{{ tool.quote.text }}”</p>
                <footer>{{ tool.quote.attribution }} · <a :href="tool.quote.sourceUrl" target="_blank" rel="noopener noreferrer">source ↗</a></footer>
              </blockquote>
              <p><strong>Skip it if:</strong> {{ tool.skipIf }}</p>
              <template v-if="tool.isUs">
                <div class="av-actions">
                  <a
                    :href="signupUrl('https://app.workspace369.com/signup?plan=voyager')"
                    class="av-btn"
                    data-conversion-event="start_free_click"
                    :data-conversion-label="`${page.h1} — tool CTA (Voyager)`"
                    :data-conversion-location="location"
                  >Start free with Voyager</a>
                </div>
                <TrialDisclosure plan="voyager" />
                <p class="av-after">
                  <NuxtLink :to="localePath('/pricing')" class="av-link" data-conversion-event="pricing_click" :data-conversion-label="`${page.h1} — tool pricing link`" :data-conversion-location="location">See every plan →</NuxtLink>
                </p>
              </template>
            </div>
          </details>
        </div>
      </div>
    </section>

    <!-- WHY PEOPLE LEAVE: dated events as a ruled timeline, then the users' own words. -->
    <section class="av-sec av-tint" aria-labelledby="av-why-title">
      <div class="av-wrap av-narrow">
        <h2 id="av-why-title">Why people leave {{ page.competitor }} in 2026</h2>
        <p v-if="page.eventsIntro" class="av-sub">{{ page.eventsIntro }}</p>
        <ol class="av-events">
          <li v-for="event in page.events" :key="event.date">
            <details>
              <summary><time>{{ event.date }}</time><h3>{{ event.title }}</h3></summary>
              <p>{{ event.body }}</p>
            </details>
          </li>
        </ol>
        <div v-if="visibleQuotes.length" class="av-quotes">
          <blockquote v-for="quote in visibleQuotes" :key="quote.text" class="av-quote">
            <p>“{{ quote.text }}”</p>
            <footer>{{ quote.attribution }} · <a :href="quote.sourceUrl" target="_blank" rel="noopener noreferrer">{{ quote.sourceLabel }} ↗</a></footer>
          </blockquote>
        </div>
      </div>
    </section>

    <!-- STAY OR SWITCH: the honest "stay" list beside the switch steps. -->
    <section class="av-sec" aria-labelledby="av-switch-title">
      <div class="av-wrap">
        <div class="av-stay-switch">
          <div class="av-stay">
            <h2 id="av-stay-title">Stay on {{ page.competitor }} if…</h2>
            <ul><li v-for="item in page.stayIf" :key="item">{{ item }}</li></ul>
          </div>
          <div>
            <h2 id="av-switch-title">How to switch from {{ page.competitor }}</h2>
            <ol class="av-steps">
              <li v-for="step in page.switchSteps" :key="step.title">
                <h3>{{ step.title }}</h3>
                <p>{{ step.body }}</p>
              </li>
            </ol>
            <p class="av-sub">{{ page.switchIntro }} <NuxtLink :to="localePath(page.switchLink.path)">{{ page.switchLink.label }} →</NuxtLink></p>
          </div>
        </div>
      </div>
    </section>
    <SwitchSave v-if="page.setupOfferEarly" :competitor-slug="page.slug" :location="location" />

    <!-- PHONE AND IPAD -->
    <section v-if="page.mobileApp" class="av-sec" aria-labelledby="av-mobile-title">
      <div class="av-wrap">
        <h2 id="av-mobile-title">On a phone or iPad</h2>
        <div class="av-two av-mobile">
          <div>
            <h3>{{ page.competitor }}</h3>
            <p>{{ page.mobileApp.intro }}</p>
            <blockquote v-for="quote in page.mobileApp.quotes" :key="quote.text" class="av-quote">
              <p>“{{ quote.text }}”</p>
              <footer>{{ quote.attribution }} · <a :href="quote.sourceUrl" target="_blank" rel="noopener noreferrer">{{ quote.sourceLabel }} ↗</a></footer>
            </blockquote>
          </div>
          <div class="av-mobile-us">
            <h3><Logo class="av-logo" aria-hidden="true" /> Workspace369</h3>
            <ul><li v-for="point in workspaceMobileApp.points" :key="point">{{ point }}</li></ul>
            <p class="av-sub">
              App Store listing, checked {{ workspaceMobileApp.store.checkedOn }}:
              <template v-for="row in workspaceMobileApp.store.rows" :key="row.device">{{ ' ' }}{{ row.device }}: {{ row.requirement }}</template>{{ ' ' }}
              <a :href="workspaceMobileApp.store.url" target="_blank" rel="noopener noreferrer">{{ workspaceMobileApp.store.label }} ↗</a>
            </p>
            <NuxtLink :to="localePath(workspaceMobileApp.featurePath)" class="av-link">See the mobile app →</NuxtLink>
          </div>
        </div>
        <p class="av-note">Quotes are from public App Store reviews of {{ page.competitor }}’s app, quoted as written. Each is one person’s experience, and the app may have changed since.</p>
      </div>
    </section>
    <section v-else-if="mobileProofPaths.has(location)" class="av-sec" aria-labelledby="av-mobile-title">
      <div class="av-wrap av-narrow">
        <h2 id="av-mobile-title">Workspace369 on iPhone and iPad</h2>
        <MobileAppProof :location="location" />
      </div>
    </section>

    <!-- FAQ, with the method (moved out of the hero) as its first answer's neighbour. -->
    <section class="av-sec av-tint" aria-labelledby="av-faq-title">
      <div class="av-wrap av-narrow">
        <h2 id="av-faq-title">Questions {{ page.competitor }} users actually ask</h2>
        <div class="av-faq">
          <details v-for="(item, i) in page.faqs" :key="item.q" :open="i === 0">
            <summary>{{ item.q }}</summary>
            <p>{{ item.a }}</p>
          </details>
        </div>
        <h3 id="av-method" class="av-method-title">How we compared</h3>
        <p class="av-sub">{{ page.methodology }}</p>
      </div>
    </section>

    <!-- CLOSE: one primary action; the Specialist path is a quiet link. -->
    <section class="av-close" aria-labelledby="av-final-title">
      <div class="av-wrap av-narrow">
        <h2 id="av-final-title">Texting and calling, inside the client record.</h2>
        <p>Texts and calls start on Voyager ($74/mo, 2 seats); IVR, call queues and call recording on Specialist ($149/mo, 10 seats). 14-day free trial — bring one client and run the full loop.</p>
        <div class="av-actions av-center">
          <a
            :href="signupUrl('https://app.workspace369.com/signup?plan=voyager')"
            class="av-btn"
            data-conversion-event="start_free_click"
            :data-conversion-label="`${page.h1} final CTA (Voyager)`"
            :data-conversion-location="location"
            :data-conversion-variant="ctaVariant || undefined"
          >Start free with Voyager</a>
        </div>
        <TrialDisclosure plan="voyager" centered />
        <p class="av-after av-center">
          <NuxtLink :to="localePath('/pricing')" class="av-link" data-conversion-event="pricing_click" :data-conversion-label="`${page.h1} final pricing link`" :data-conversion-location="location">Need IVR? Compare plans →</NuxtLink>
        </p>
      </div>
    </section>

    <section class="av-sec" aria-labelledby="av-related-title">
      <div class="av-wrap">
        <h2 id="av-related-title">Related pages</h2>
        <ul class="av-related">
          <li v-for="link in page.relatedLinks" :key="link.path">
            <NuxtLink :to="localePath(link.path)"><strong>{{ link.label }}</strong><small>{{ link.description }}</small></NuxtLink>
          </li>
        </ul>
        <h2 v-if="page.sources?.length" id="av-sources-title" class="av-sources-title">Sources</h2>
        <details v-if="page.sources?.length" class="av-sources">
          <summary>All {{ page.sources.length }} pages we read</summary>
          <ul>
            <li v-for="source in page.sources" :key="source.url"><a :href="source.url" target="_blank" rel="noopener noreferrer">{{ source.label }} ↗</a></li>
          </ul>
        </details>
      </div>
    </section>

    <div class="av-sticky" :class="{ 'is-off': overFooter }" :aria-hidden="overFooter ? 'true' : undefined" :inert="overFooter || undefined">
      <div>
        <strong>SMS + calls built in</strong>
        <TrialDisclosure compact plan="voyager" />
      </div>
      <div class="av-sticky-actions">
        <NuxtLink
          :to="localePath('/pricing')"
          class="av-sticky-pricing"
          data-conversion-event="pricing_click"
          :data-conversion-label="`${page.h1} sticky pricing`"
          :data-conversion-location="location"
        >{{ localeUi.nav.pricing }}</NuxtLink>
      <a
        :href="signupUrl('https://app.workspace369.com/signup?plan=voyager')"
        class="av-sticky-btn"
        data-conversion-event="start_free_click"
        :data-conversion-label="`${page.h1} sticky CTA (Voyager)`"
        :data-conversion-location="location"
      >Try Voyager free</a>
      </div>
    </div>
  </main>
</template>

<script setup>
import Logo from '~/components/Logo.vue';
import { localizedUrl } from '~/data/locales';
import { mobileProofPaths, workspaceMobileApp } from '~/data/mobile-app-facts';

const props = defineProps({
  page: {
    type: Object,
    required: true,
  },
});

const { addReferralToUrl } = useReferral();
// Pages that set utmSource tag their sign-up links so the source survives without cookie consent.
const signupUrl = (href) => {
  const url = new URL(href);
  if (!props.page.utmSource || url.origin !== 'https://app.workspace369.com' || url.pathname !== '/signup') return addReferralToUrl(href);
  url.searchParams.set('utm_source', props.page.utmSource);
  url.searchParams.set('utm_medium', 'organic_page');
  return addReferralToUrl(url.toString());
};
const { canonicalLocaleCode, localePath, localeUi } = useWorkspaceLocale();
// Q152: the bar sat over the site footer's links at the page bottom. Slide it away while the footer is on screen,
// as the compare bar does. Starts shown (SSR), so nothing shifts; it is position: fixed either way.
const overFooter = ref(false);
let footerObserver = null;
onMounted(() => {
  const footer = document.querySelector('footer.site-footer');
  if (!footer) return;
  footerObserver = new IntersectionObserver(([entry]) => { overFooter.value = entry.isIntersecting; });
  footerObserver.observe(footer);
});
onBeforeUnmount(() => footerObserver?.disconnect());


const location = computed(() => `/alternatives/${props.page.slug}`);
// The hero runs the CTA copy test on the mobile-proof pages (composables/useCtaTest.ts).
const { enrolled: ctaEnrolled, variant: ctaVariant, label: ctaLabel } = useCtaTest(location);
const canonicalUrl = computed(() => localizedUrl('https://workspace369.com', location.value, canonicalLocaleCode.value));

const h1Parts = computed(() => {
  const i = props.page.h1.indexOf(': ');
  return i > 0 ? [props.page.h1.slice(0, i), props.page.h1.slice(i + 2)] : null;
});

const columns = [
  { key: 'price', label: 'Price (tier most buyers need)' },
  { key: 'seats', label: 'Seats' },
  { key: 'sms', label: 'SMS' },
  { key: 'voice', label: 'Voice / phone' },
  { key: 'accounting', label: 'Built-in accounting' },
  { key: 'portal', label: 'Client portal' },
  { key: 'paymentPlans', label: 'Payment plans' },
  { key: 'timeTracking', label: 'Time tracking' },
  { key: 'checkedOn', label: 'Last checked' },
];
// Last checked sits under each tool's name, so the table keeps the decision columns.
const tableColumns = columns.filter((column) => column.key !== 'checkedOn');

const alternativeTools = computed(() => props.page.tools.filter((tool) => !tool.baseline));
const visibleQuotes = computed(() => (props.page.quotes || []).filter((quote) => !quote.needsReview));

const isExternal = (url) => /^https?:\/\//.test(url);
const notListedLabel = (tool) =>
  `Not listed on ${tool.name}'s pricing page${tool.checkedOn ? ` (checked ${tool.checkedOn})` : ''}`;
const hostOf = (url) => {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
};
const slugify = (name) => name.toLowerCase().replace(/[^a-z0-9]+/g, '-');
</script>

<style scoped>
/* The shipped compare template's approved neutral style (ComparisonLandingPage.vue "Proposal restyle"):
   #171717 primary, #e6e6e6 hairlines, 6px controls, 8-10px surfaces, no kickers, no pills. Light and dark. */
.av {
  --c-ink: #0a0a0a; --c-ink2: #4f4f4f; --c-ink3: #666666; --c-edge: #e6e6e6; --c-canvas: #f7f7f7;
  --c-page: #fff; --c-primary: #171717; --c-on-primary: #fafafa; --c-us: rgb(10 10 10 / 0.04);
  background: var(--c-page);
  color: var(--c-ink);
}
.dark .av {
  --c-ink: #fafafa; --c-ink2: #c2c2c2; --c-ink3: #9a9a9a; --c-edge: #2c2c2c; --c-canvas: #1a1a1a;
  --c-page: #121212; --c-primary: #fafafa; --c-on-primary: #171717; --c-us: rgb(250 250 250 / 0.06);
}
.av :is(h1, h2, h3, p, td, th, li):not(:lang(en)) { hyphens: manual; overflow-wrap: break-word; }
.av-wrap { max-width: 78rem; margin-inline: auto; padding-inline: 1rem; }
@media (min-width: 640px) { .av-wrap { padding-inline: 1.75rem; } }
.av-narrow { max-width: 78rem; }
.av-narrow > * { max-width: 52rem; }
.av-narrow > p, .av-sub, .av-quick, .av-faq { max-width: 48rem; }
.av-wide { max-width: 78rem; }
.av-wide > .av-sub { max-width: 52rem; }

.av h1 { font-weight: 780; letter-spacing: -0.01em; line-height: 1.05; font-size: clamp(1.75rem, 4.2vw, 3.1rem); text-wrap: balance; }
.av h2 { font-size: clamp(1.4rem, 2.5vw, 1.85rem); font-weight: 750; letter-spacing: -0.01em; line-height: 1.15; text-wrap: balance; }
.av h3 { font-size: 1rem; font-weight: 700; line-height: 1.35; }
.av a { text-underline-offset: 3px; }

/* HERO */
.av-hero { padding: 7.5rem 0 2rem; }
@media (min-width: 768px) { .av-hero { padding: 8.5rem 0 2.5rem; } }
/* Phones: H1, then the product, then the answer and the button (DOM order stays text-first for readers and search).
   Desktop: copy on the left, the app on the right. */
.av-hero-grid { max-width: 78rem; display: grid; grid-template-columns: minmax(0, 1fr); grid-template-areas: "top" "shot" "copy"; gap: 1.25rem; }
.av-hero-top { grid-area: top; }
.av-hero-copy { grid-area: copy; }
.av-hero-shot { grid-area: shot; }
.av-hero-copy .av-answer { margin-top: 0; }
@media (min-width: 1100px) {
  .av-hero-grid { grid-template-columns: minmax(0, 32rem) minmax(0, 1fr); grid-template-areas: "top shot" "copy shot"; grid-template-rows: auto 1fr; column-gap: 3.5rem; row-gap: 1rem; }
  .av-hero-shot { align-self: start; }
}
.av-hero-note { max-width: 78rem; }
.av-crumb { display: flex; gap: 0.45rem; margin-bottom: 0.9rem; color: var(--c-ink3); font-size: 0.85rem; font-weight: 500; }
.av-crumb a:hover { color: var(--c-ink); }
.av-answer { margin-top: 1rem; color: var(--c-ink2); font-size: 1.02rem; line-height: 1.6; }
.av-actions { display: flex; flex-wrap: wrap; align-items: center; gap: 0.75rem 1.25rem; margin: 1.4rem 0 0.7rem; }
.av-center { justify-content: center; }
/* The pricing link sits under the trial line, so the terms stay directly under the button (legal B1.1). */
.av-after { display: flex; margin: 0.6rem 0 0; }
.av-close .av-narrow > * { margin-inline: auto; }
.av-btn { display: inline-flex; align-items: center; justify-content: center; min-height: 2.85rem; border-radius: 6px; background: var(--c-primary); color: var(--c-on-primary); padding: 0.7rem 1.35rem; font-size: 0.95rem; font-weight: 650; }
.av-btn:hover { opacity: 0.9; }
.av-link { display: inline-block; padding-block: 0.7rem; color: var(--c-ink); font-size: 0.95rem; font-weight: 600; } /* 44px+ tap target (was 29px) */
.av-link:hover { text-decoration: underline; }
.av-byline { display: flex; align-items: center; gap: 0.6rem; margin-top: 1.1rem; color: var(--c-ink3); font-size: 0.85rem; line-height: 1.45; }
.av-byline-logo { width: 1.6rem; height: 1.6rem; flex-shrink: 0; }
.av-logo { display: inline-block; width: 1.1em; height: 1.1em; vertical-align: -0.18em; }
/* The real inbox, pre-cropped to 4:3: the conversation list and the SMS thread. */
.av-hero-shot { margin: 0; overflow: hidden; border: 1px solid var(--c-edge); border-radius: 10px; background: var(--c-canvas); box-shadow: 0 30px 60px -30px rgb(15 23 42 / 0.22); aspect-ratio: 16 / 12; }
.av-hero-shot picture { display: block; }
.av-hero-shot img { display: block; width: 100%; height: auto; }
.av-shot-dark { display: none !important; }
.dark .av-shot-light { display: none !important; }
.dark .av-shot-dark { display: block !important; }
.dark .av :deep(.competitor-notice) { color: #cbd5e1; }
.dark .av :deep(.competitor-notice a) { color: var(--c-ink); }
.dark .av-hero-shot { border-color: #3a3a3a; box-shadow: none; }
.av-hero-note { margin-top: 1.5rem; }

/* SECTIONS */
.av-sec { padding: 3.25rem 0; border-top: 1px solid var(--c-edge); }
@media (min-width: 768px) { .av-sec { padding: 4.25rem 0; } }
.av-tint { background: var(--c-canvas); }
.av-sub, .av-note { margin-top: 0.6rem; color: var(--c-ink2); font-size: 0.93rem; line-height: 1.6; }
.av-note { color: var(--c-ink3); font-size: 0.85rem; }
.av-pick { margin-top: 1.5rem; max-width: 48rem; }
.av-pick h3 { font-size: 1.05rem; font-weight: 650; color: var(--c-ink); }
.av-pick p { margin-top: 0.4rem; line-height: 1.65; color: var(--c-ink2); }
.av-sub a, .av-note a, .av-quick a { color: var(--c-ink); font-weight: 600; text-decoration: underline; }
.av-src { color: var(--c-ink3); font-size: 0.85rem; white-space: nowrap; }
.av-lede { margin-top: 0.9rem; color: var(--c-ink2); line-height: 1.65; }

/* SHORT VERSION: a ruled list, not cards. */
.av-quick { margin-top: 1.25rem; border-top: 1px solid var(--c-edge); }
.av-quick li { padding: 0.9rem 0; border-bottom: 1px solid var(--c-edge); line-height: 1.6; }

/* TABLE. Desktop: a full-width table, the Workspace369 row tinted. Phones: each tool stacks, with the
   column label above each value, so the page never scrolls sideways and nothing scrolls inside it. */
.av-table { width: 100%; margin-top: 1.5rem; border-collapse: collapse; font-size: 0.86rem; line-height: 1.45; background: var(--c-page); }
.av-table th, .av-table td { padding: 0.75rem 0.7rem; border-bottom: 1px solid var(--c-edge); text-align: left; vertical-align: top; }
.av-table thead th { color: var(--c-ink2); font-size: 0.82rem; font-weight: 650; }
.av-table tbody th { min-width: 8.5rem; }
.av-table td { color: var(--c-ink2); }
.av-tool { display: block; font-weight: 700; color: var(--c-ink); }
.av-tag { display: inline-block; margin-top: 0.25rem; border: 1px solid var(--c-edge); border-radius: 4px; padding: 0 0.35rem; color: var(--c-ink3); font-size: 0.72rem; font-weight: 600; }
.av-tag-us { border-color: var(--c-primary); color: var(--c-ink); }
.av-us { background: var(--c-us); }
.av-us td { color: var(--c-ink); }
.av-base td { color: var(--c-ink3); }
.av-yes { font-weight: 650; color: var(--c-ink); }
.av-dash { color: var(--c-ink3); text-decoration: none; }
.av-m { display: none; }
@media (max-width: 899px) {
  .av-table thead { display: none; }
  .av-table, .av-table tbody, .av-table tr, .av-table th, .av-table td { display: block; }
  .av-table { background: transparent; }
  .av-table tr { margin-top: 0.75rem; border: 1px solid var(--c-edge); border-radius: 8px; background: var(--c-page); padding: 0.4rem 0.9rem 0.7rem; }
  .av-table tr.av-us { border-color: var(--c-primary); }
  .av-table tbody th { min-width: 0; border: 0; padding: 0.5rem 0 0.35rem; font-size: 1rem; }
  .av-table td { display: grid; grid-template-columns: 6.25rem minmax(0, 1fr); gap: 0.6rem; border: 0; border-top: 1px solid var(--c-edge); padding: 0.35rem 0; font-size: 0.82rem; line-height: 1.4; }
  .av-m { display: block; color: var(--c-ink3); font-size: 0.78rem; font-weight: 600; }
}

/* TOOLS: ruled disclosures. */
.av-tools { margin-top: 1.25rem; border-top: 1px solid var(--c-edge); }
.av-tool-row { border-bottom: 1px solid var(--c-edge); }
.av-tool-row summary { display: grid; grid-template-columns: minmax(0, 1fr) auto; gap: 1rem; align-items: start; padding: 1rem 0; cursor: pointer; list-style: none; }
.av-tool-row summary::-webkit-details-marker { display: none; }
.av-tool-row summary::after { content: "+"; grid-column: 2; grid-row: 1; justify-self: end; color: var(--c-ink3); font-weight: 700; }
.av-tool-row[open] summary::after { content: "–"; }
.av-tool-head strong { display: block; font-size: 1.05rem; }
.av-tool-head span { display: block; margin-top: 0.2rem; color: var(--c-ink2); font-size: 0.9rem; line-height: 1.5; }
.av-tool-price { grid-column: 1; color: var(--c-ink); font-size: 0.95rem; font-weight: 650; font-variant-numeric: tabular-nums; }
@media (min-width: 720px) {
  .av-tool-row summary { grid-template-columns: minmax(0, 1fr) 14rem 1rem; }
  .av-tool-price { grid-column: 2; grid-row: 1; text-align: right; }
  .av-tool-row summary::after { grid-column: 3; }
}
.av-tool-us summary .av-tool-head strong::after { content: " · ours"; color: var(--c-ink3); font-weight: 500; font-size: 0.85rem; }
.av-tool-body { padding: 0 0 1.25rem; color: var(--c-ink2); line-height: 1.6; }
.av-tool-body > * + * { margin-top: 0.75rem; }
.av-tool-body strong { color: var(--c-ink); }
.av-two { display: grid; gap: 1rem; }
@media (min-width: 720px) { .av-two { grid-template-columns: 1fr 1fr; gap: 2rem; } }
.av-two h3 { margin-bottom: 0.35rem; color: var(--c-ink); font-size: 0.9rem; }
.av-two ul { display: grid; gap: 0.35rem; }
.av-two li, .av-stay li { position: relative; padding-left: 1rem; }
.av-two li::before, .av-stay li::before { content: ""; position: absolute; left: 0; top: 0.7em; width: 0.4rem; height: 1px; background: var(--c-ink3); }

/* QUOTES AND EVENTS */
.av-quote { margin: 0; border-left: 2px solid var(--c-edge); padding: 0.1rem 0 0.1rem 0.9rem; }
.av-quote p { color: var(--c-ink); font-size: 0.95rem; line-height: 1.55; }
.av-quote footer { margin-top: 0.3rem; color: var(--c-ink3); font-size: 0.8rem; }
.av-quote footer a { color: var(--c-ink3); text-decoration: underline; }
.av-events { margin-top: 1.25rem; border-top: 1px solid var(--c-edge); }
.av-events li { display: grid; gap: 0.3rem; padding: 1rem 0; border-bottom: 1px solid var(--c-edge); }
@media (min-width: 720px) { .av-events li { grid-template-columns: 8rem minmax(0, 1fr); gap: 1.5rem; } }
.av-events time { color: var(--c-ink3); font-size: 0.85rem; font-weight: 600; font-variant-numeric: tabular-nums; }
.av-events p { margin-top: 0.3rem; color: var(--c-ink2); line-height: 1.6; }
.av-quotes { display: grid; gap: 1rem; margin-top: 1.75rem; }
@media (min-width: 720px) { .av-quotes { grid-template-columns: 1fr 1fr; gap: 1.25rem 2rem; } }

/* STAY / SWITCH */
.av-stay-switch { display: grid; gap: 2.5rem; }
@media (min-width: 900px) { .av-stay-switch { grid-template-columns: minmax(0, 1fr) minmax(0, 1.3fr); gap: 3.5rem; } }
.av-stay ul { display: grid; gap: 0.6rem; margin-top: 1rem; color: var(--c-ink2); line-height: 1.55; }
.av-steps { margin-top: 1rem; counter-reset: s; border-top: 1px solid var(--c-edge); }
.av-steps li { position: relative; padding: 0.9rem 0 0.9rem 2.2rem; border-bottom: 1px solid var(--c-edge); counter-increment: s; }
.av-steps li::before { content: counter(s); position: absolute; left: 0; top: 0.85rem; color: var(--c-ink3); font-weight: 700; font-variant-numeric: tabular-nums; }
.av-steps p { margin-top: 0.25rem; color: var(--c-ink2); line-height: 1.55; font-size: 0.93rem; }

/* MOBILE */
.av-mobile { margin-top: 1.25rem; }
.av-mobile > div { display: grid; align-content: start; gap: 0.75rem; color: var(--c-ink2); line-height: 1.55; }
.av-mobile-us { border: 1px solid var(--c-edge); border-radius: 10px; padding: 1.1rem 1.25rem; background: var(--c-canvas); }
.av-mobile-us ul { display: grid; gap: 0.4rem; }
.av-mobile-us li { position: relative; padding-left: 1rem; }
.av-mobile-us li::before { content: ""; position: absolute; left: 0; top: 0.7em; width: 0.4rem; height: 1px; background: var(--c-ink3); }

/* FAQ */
.av-faq { margin-top: 1.25rem; border-top: 1px solid var(--c-edge); }
.av-faq details { border-bottom: 1px solid var(--c-edge); }
.av-faq summary { display: flex; justify-content: space-between; gap: 1rem; padding: 0.95rem 0; cursor: pointer; font-weight: 650; list-style: none; }
.av-faq summary::-webkit-details-marker { display: none; }
.av-faq summary::after { content: "+"; color: var(--c-ink3); font-weight: 700; }
.av-faq details[open] summary::after { content: "–"; }
.av-faq p { padding-bottom: 1rem; color: var(--c-ink2); line-height: 1.65; }
.av-method-title { margin-top: 2rem; }

/* CLOSE, RELATED, SOURCES */
.av-close { padding: 4.25rem 0; border-top: 1px solid var(--c-edge); text-align: center; }
.av-close p { margin: 0.75rem auto 0; max-width: 36rem; color: var(--c-ink2); line-height: 1.6; }
.av-related { display: grid; gap: 0; margin-top: 1.25rem; border-top: 1px solid var(--c-edge); }
@media (min-width: 720px) { .av-related { grid-template-columns: 1fr 1fr; column-gap: 2rem; } }
.av-related a { display: block; padding: 0.8rem 0; border-bottom: 1px solid var(--c-edge); }
.av-related strong { display: block; font-size: 0.95rem; }
.av-related small { display: block; margin-top: 0.15rem; color: var(--c-ink3); font-size: 0.85rem; line-height: 1.45; }
.av-related a:hover strong { text-decoration: underline; }
.av-sources { margin-top: 1.75rem; color: var(--c-ink2); font-size: 0.88rem; }
.av-sources summary { cursor: pointer; font-weight: 650; color: var(--c-ink); }
.av-sources ul { display: grid; gap: 0.3rem; margin-top: 0.6rem; }
.av-sources a { color: var(--c-ink2); text-decoration: underline; }

/* PHONE STICKY BAR (same rules as today's template, neutral colours). */
.av-sticky { position: fixed; inset-inline: 0; bottom: 0; z-index: 40; display: flex; align-items: center; justify-content: space-between; gap: 0.9rem; border-top: 1px solid var(--c-edge); background: color-mix(in srgb, var(--c-page) 94%, transparent); padding: 0.7rem 1rem calc(0.7rem + env(safe-area-inset-bottom)); backdrop-filter: blur(12px); }
.av-sticky > div { min-width: 0; }
.av-sticky strong { display: block; overflow: hidden; color: var(--c-ink); font-size: 0.82rem; font-weight: 700; text-overflow: ellipsis; white-space: nowrap; }
/* Same rule as the live template: the trial line shows at every width and never under 12px;
   below 480px the title (it repeats the H1) gives way to it. */
.av-sticky :deep(.trial-disclosure) { margin: 0; color: var(--c-ink2); line-height: 1.35; }
.av-sticky-btn { flex-shrink: 0; display: inline-flex; align-items: center; min-height: 2.75rem; /* 44px tap target */ border-radius: 6px; background: var(--c-primary); color: var(--c-on-primary); padding: 0.5rem 1rem; font-size: 0.84rem; font-weight: 650; white-space: nowrap; }
@media (max-width: 479px) { .av-sticky strong { display: none; } }
/* Wherever the bar shows (under 820px): the button, then its terms line directly under it (B1.1), as in PhoneStickyCta.
   display: contents lets the heading and the terms line be ordered around the button. */
@media (max-width: 819px) {
  .av-sticky { flex-direction: column; align-items: stretch; gap: 0.4rem; }
  .av-sticky > div { display: contents; }
  .av-sticky strong { text-align: center; }
  /* Full width on phones; capped and centred on tablets so it reads as a button, not a banner. */
  .av-sticky-btn { order: 1; justify-content: center; width: 100%; max-width: 24rem; align-self: center; }
  .av-sticky :deep(.trial-disclosure) { order: 2; max-width: none; text-align: center; }
}
/* Pricing beside the trial button, as in PhoneStickyCta (Q088 board, 2026-11-01). Both 44px+ tall. */
.av-sticky-pricing { display: inline-flex; align-items: center; justify-content: center; min-height: 2.875rem; border: 1px solid var(--c-edge); border-radius: 6px; color: var(--c-ink); padding: 0.5rem 1rem; font-size: 0.84rem; font-weight: 650; text-align: center; line-height: 1.15; }
@media (max-width: 819px) {
  .av-sticky > .av-sticky-actions { order: 1; display: grid; grid-template-columns: auto minmax(0, 1fr); gap: 0.6rem; width: 100%; max-width: 24rem; align-self: center; }
  .av-sticky-actions .av-sticky-btn { max-width: none; min-height: 2.875rem; }
}
.av-sticky { transition: transform 0.2s ease, visibility 0.2s; }
.av-sticky.is-off { transform: translateY(110%); visibility: hidden; }
@media (prefers-reduced-motion: reduce) { .av-sticky { transition: none; } }
@media (min-width: 820px) { .av-sticky { display: none; } }
@media (max-width: 819px) { .av { padding-bottom: 4.25rem; } }

.av-h1-main { display: block; }
.av-h1-rest { display: block; margin-top: 0.5rem; color: var(--c-ink2); font-size: 0.5em; font-weight: 600; letter-spacing: 0; line-height: 1.3; }
.av-byline a { color: var(--c-ink); text-decoration: underline; }
.av-tool-meta { display: block; margin-top: 0.3rem; color: var(--c-ink3); font-size: 0.78rem; font-weight: 500; }
.av-tool-meta a { color: var(--c-ink2); text-decoration: underline; }
.av-tool-limit { color: var(--c-ink2); }
.av-tool-limit b { color: var(--c-ink); font-weight: 650; }
.av-more { margin-top: 1.25rem; }
.av-more > summary, .av-events summary { cursor: pointer; min-height: 44px; display: flex; align-items: center; gap: 0.75rem; font-weight: 650; color: var(--c-ink); }
.av-events li { display: block; padding: 0; }
.av-events summary { padding: 0.6rem 0; flex-wrap: wrap; }
.av-events summary h3 { font-size: 1rem; }
.av-events details > p { padding-bottom: 0.9rem; }
.av-sources-title { margin-top: 2.5rem; }
.av-tools summary, .av-faq summary { min-height: 44px; }
</style>
